import React, { useRef, useState, useEffect, ReactElement } from "react";
import { Link } from "react-router-dom";
import { Check, Close, Info } from "@mui/icons-material";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Alert,
  Container,
  InputAdornment,
} from "@mui/material";
import { API_ENDPOINTS } from "../config";

const USER_REGEX = /^[A-z][A-z0-9-_]{3,23}$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const Register = (): ReactElement => {
  const userRef = useRef<HTMLInputElement>(null);
  const errRef = useRef<HTMLDivElement>(null);

  const [user, setUser] = useState<string>("");
  const [validName, setValidName] = useState<boolean>(false);
  const [userFocus, setUserFocus] = useState<boolean>(false);

  const [pwd, setPwd] = useState<string>("");
  const [validPwd, setValidPwd] = useState<boolean>(false);
  const [pwdFocus, setPwdFocus] = useState<boolean>(false);

  const [matchPwd, setMatchPwd] = useState<string>("");
  const [validMatch, setValidMatch] = useState<boolean>(false);
  const [matchFocus, setMatchFocus] = useState<boolean>(false);

  const [email, setEmail] = useState<string>("");
  const [validEmail, setValidEmail] = useState<boolean>(false);
  const [emailFocus, setEmailFocus] = useState<boolean>(false);

  const [errMsg, setErrMsg] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  useEffect(() => {
    setValidName(USER_REGEX.test(user));
  }, [user]);

  useEffect(() => {
    setValidPwd(PWD_REGEX.test(pwd));
    setValidMatch(pwd === matchPwd);
  }, [pwd, matchPwd]);

  useEffect(() => {
    setValidEmail(EMAIL_REGEX.test(email));
  }, [email]);

  useEffect(() => {
    setErrMsg("");
  }, [user, pwd, matchPwd, email]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v1 = USER_REGEX.test(user);
    const v2 = PWD_REGEX.test(pwd);
    const v3 = EMAIL_REGEX.test(email);
    if (!v1 || !v2 || !v3) {
      setErrMsg("Invalid Entry");
      return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.REGISTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user, password: pwd, email }),
      });
      if (!response.ok) {
        throw new Error("Registration failed");
      }
      await response.json();
      setSuccess(true);
      setUser("");
      setPwd("");
      setMatchPwd("");
      setEmail("");
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : "Registration failed");
      errRef.current?.focus();
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              borderRadius: 2,
            }}
          >
            <Typography
              component="h1"
              variant="h4"
              sx={{ mb: 3, fontWeight: "bold", color: "success.main" }}
            >
              Success!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your account has been created successfully.
            </Typography>
            <Button
              component={Link}
              to="/auth"
              variant="contained"
              color="primary"
              sx={{ textTransform: "none" }}
            >
              Sign In
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            borderRadius: 2,
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{ mb: 3, fontWeight: "bold" }}
          >
            Register
          </Typography>

          {errMsg && (
            <Alert ref={errRef} severity="error" sx={{ width: "100%", mb: 2 }}>
              {errMsg}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={user}
              onChange={(e) => setUser(e.target.value)}
              onFocus={() => setUserFocus(true)}
              onBlur={() => setUserFocus(false)}
              error={!!(userFocus && user && !validName)}
              helperText={
                userFocus && user && !validName ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Info fontSize="small" />
                    <span>
                      4 to 24 characters. Must begin with a letter. Letters,
                      numbers, underscores, hyphens allowed.
                    </span>
                  </Box>
                ) : null
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {user &&
                      (validName ? (
                        <Check color="success" fontSize="small" />
                      ) : (
                        <Close color="error" fontSize="small" />
                      ))}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#eeeeee",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailFocus(true)}
              onBlur={() => setEmailFocus(false)}
              error={!!(emailFocus && email && !validEmail)}
              helperText={
                emailFocus && email && !validEmail ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Info fontSize="small" />
                    <span>Must be a valid email address.</span>
                  </Box>
                ) : null
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {email &&
                      (validEmail ? (
                        <Check color="success" fontSize="small" />
                      ) : (
                        <Close color="error" fontSize="small" />
                      ))}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#eeeeee",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              onFocus={() => setPwdFocus(true)}
              onBlur={() => setPwdFocus(false)}
              error={!!(pwdFocus && pwd && !validPwd)}
              helperText={
                pwdFocus && pwd && !validPwd ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Info fontSize="small" />
                    <span>
                      8 to 24 characters. Must include uppercase and lowercase
                      letters, a number and a special character. Allowed special
                      characters: ! @ # $ %
                    </span>
                  </Box>
                ) : null
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {pwd &&
                      (validPwd ? (
                        <Check color="success" fontSize="small" />
                      ) : (
                        <Close color="error" fontSize="small" />
                      ))}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#eeeeee",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirm_pwd"
              autoComplete="new-password"
              value={matchPwd}
              onChange={(e) => setMatchPwd(e.target.value)}
              onFocus={() => setMatchFocus(true)}
              onBlur={() => setMatchFocus(false)}
              error={!!(matchFocus && matchPwd && !validMatch)}
              helperText={
                matchFocus && matchPwd && !validMatch ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Info fontSize="small" />
                    <span>Must match the first password input field.</span>
                  </Box>
                ) : null
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {matchPwd &&
                      (validMatch ? (
                        <Check color="success" fontSize="small" />
                      ) : (
                        <Close color="error" fontSize="small" />
                      ))}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#eeeeee",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#ffffff",
                  },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!validName || !validPwd || !validMatch || !validEmail}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: "1.1rem",
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              Sign Up
            </Button>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2">
                Already registered?{" "}
                <Link
                  to="/auth"
                  style={{
                    color: "#1976d2",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
